import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

/**
 * Branded empty-state component used across pages that can have no data.
 *
 * Visual: cream "paper" surface with a gold-hairline frame. The icon
 * sits inside a gold-edged crest medallion so the empty state always
 * reads as part of the brand, never as "nothing here." A small
 * gold-gradient star ornament floats top-right to add editorial
 * texture, and the divider above the CTA row is a fading gold hairline.
 *
 * Usage:
 *   <EmptyState
 *     icon={<Dog size={32} weight="duotone" />}
 *     title="No dogs yet"
 *     description="Register your first dog to start your pedigree."
 *     ctaLabel="Register a dog"
 *     ctaTo="/dogs/new"
 *   />
 */
export default function EmptyState({
  icon = null,
  title,
  description,
  ctaLabel = null,
  ctaTo = null,
  ctaOnClick = null,
  secondaryLabel = null,
  secondaryTo = null,
  testId = "empty-state",
}) {
  return (
    <div
      data-testid={testId}
      className="relative rounded-2xl bg-cream/80 border border-[#E2DAC2] px-6 py-16 text-center overflow-hidden"
    >
      {/* gold hairline top + bottom — frames the empty state like a stamp */}
      <div className="absolute inset-x-8 top-0 h-px bg-gold-hairline" />
      <div className="absolute inset-x-8 bottom-0 h-px bg-gold-hairline" />
      {/* corner ornament */}
      <span aria-hidden="true" className="absolute top-4 right-5 text-gold/55 text-base font-spectral leading-none">✦</span>
      <span aria-hidden="true" className="absolute bottom-4 left-5 text-gold/30 text-base font-spectral leading-none">✦</span>

      {icon && (
        <div className="relative mx-auto mb-5 h-16 w-16 rounded-full bg-white text-gold flex items-center justify-center ring-1 ring-gold/40 shadow-[0_4px_18px_rgba(201,162,39,0.18)]">
          {icon}
        </div>
      )}
      <h3 className="font-spectral font-light text-2xl md:text-3xl tracking-tight">{title}</h3>
      {description && (
        <p className="text-sm text-[#3a3a3a] mt-3 max-w-md mx-auto leading-relaxed">
          {description}
        </p>
      )}
      {(ctaLabel || secondaryLabel) && (
        <>
          <div className="gold-hairline mt-7 mb-6 max-w-xs mx-auto" />
          <div className="flex flex-wrap items-center justify-center gap-2">
            {ctaLabel && (ctaTo ? (
              <Button asChild className="bg-ink hover:bg-crimson text-white rounded-full h-10 px-5">
                <Link to={ctaTo} data-testid={`${testId}-cta`}>{ctaLabel}</Link>
              </Button>
            ) : (
              <Button
                onClick={ctaOnClick}
                data-testid={`${testId}-cta`}
                className="bg-ink hover:bg-crimson text-white rounded-full h-10 px-5"
              >
                {ctaLabel}
              </Button>
            ))}
            {secondaryLabel && secondaryTo && (
              <Button asChild variant="outline" className="rounded-full h-10 px-5 border-[#E2DAC2] hover:border-gold/55 hover:bg-white">
                <Link to={secondaryTo} data-testid={`${testId}-secondary`}>{secondaryLabel}</Link>
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
